import type {
  SelectModeIframeMessage,
  SelectModeParentMessage,
} from "./generated/selectModeIframeRpc";

import * as z from "zod";

import {
  displayModeSchema,
  selectableDataSchema,
  selectedElementSchema,
  selectModeBoundingRectSchema,
  selectModeClickPositionSchema,
  selectModeFormatCommandSchema,
  selectModeHoverRectSchema,
  selectModeOptimisticUpdatePayloadSchema,
  selectModeSelectionRectSchema,
  selectModeSelectionTypeSchema,
  selectModeTiptapActiveMarksSchema,
} from "./selectMode";

// Editor↔runtime channel. Sandboxes auto-upgrade the runtime while open
// editors keep their bundle, and an editor reloads only when a channel it knows
// reports a newer peer version. So bump the version for a breaking change;
// never rename or replace the channel.
export const SELECT_MODE_IFRAME_RPC_CHANNEL_ID =
  "replo:select-mode-iframe-rpc" as const;
export const SELECT_MODE_IFRAME_RPC_CHANNEL_VERSION = 1 as const;

export const inlineEditFeedbackSchema = z
  .object({
    status: z.enum(["pending", "unavailable", "failed"]),
    rect: selectModeBoundingRectSchema,
  })
  .describe("InlineEditFeedback");

export const iframeRuntimeBuildMetadataSchema = z
  .object({
    packageName: z.string(),
    version: z.string(),
    branch: z.string().nullable(),
    commit: z.string().nullable(),
    builtAt: z.string().nullable(),
    // Features this runtime implements beyond the base protocol; the parent sends their methods only when listed.
    capabilities: z.array(z.string()).optional(),
  })
  .describe("IframeRuntimeBuildMetadata");

/** Runtimes listing this draw section insert and drag controls on the canvas and accept `setSectionCanvas`. */
export const SECTION_CANVAS_CAPABILITY = "sectionCanvas";

export const sectionCanvasSectionSchema = z
  .object({
    index: z.number().int().nonnegative(),
    // 1-based, inclusive lines of the section's JSX in the page file.
    startLine: z.number().int().positive(),
    endLine: z.number().int().positive(),
    label: z.string(),
    // Conditional or repeated in code: shown, but never moved or removed on the canvas.
    isLocked: z.boolean(),
  })
  .describe("SectionCanvasSection");

/**
 * Whether a source location's file is the canvas's page file. Locations arrive as `file://` URLs,
 * `./`-relative or absolute paths, and the page file is workspace-relative, so either may be a path
 * suffix of the other. The builder's selection sync and the runtime's section finding both use this.
 */
export function isSectionCanvasPageFile({
  filePath,
  pageFilePath,
}: {
  filePath: string;
  pageFilePath: string;
}) {
  const normalized = filePath.replace(/^file:\/\//u, "").replace(/^\.\//u, "");
  return (
    normalized === pageFilePath ||
    normalized.endsWith(`/${pageFilePath}`) ||
    pageFilePath.endsWith(`/${normalized}`)
  );
}

export const sectionCanvasStateSchema = z
  .object({
    // Workspace-relative path of the editable page file the sections were read from.
    pageFilePath: z.string(),
    sections: z.array(sectionCanvasSectionSchema),
    previewScale: z.number().positive(),
    // Shown but inert, e.g. while an agent turn or another section edit is running.
    isDisabled: z.boolean(),
    // Where the open section picker will insert, marked on the canvas until it closes.
    insertingAt: z.number().int().nonnegative().nullish(),
    // How far the builder's floating panels cover the preview's left and right edges, in preview
    // pixels: controls stay out from under them. Absent from older builders.
    occludedInsets: z
      .object({
        left: z.number().nonnegative(),
        right: z.number().nonnegative(),
      })
      .nullish(),
  })
  .describe("SectionCanvasState");

export const sectionCanvasRequestSchema = z
  .discriminatedUnion("type", [
    z.object({ type: z.literal("insert"), at: z.number().int().nonnegative() }),
    z.object({
      type: z.literal("move"),
      moveId: z.string(),
      index: z.number().int().nonnegative(),
      to: z.number().int().nonnegative(),
    }),
    z.object({
      type: z.literal("remove"),
      index: z.number().int().nonnegative(),
    }),
    // Keyboard shortcuts pressed while focus is in the preview.
    z.object({ type: z.literal("undo") }),
    z.object({ type: z.literal("redo") }),
    z.object({ type: z.literal("cancelInsert") }),
  ])
  .describe("SectionCanvasRequest");

export const sectionCanvasLayoutEntrySchema = z
  .object({
    index: z.number().int().nonnegative(),
    startLine: z.number().int().positive(),
    endLine: z.number().int().positive(),
    // Document coordinates in the preview's own (unscaled) pixels.
    rect: selectModeBoundingRectSchema,
  })
  .describe("SectionCanvasLayoutEntry");

export const selectModeEventInputSchemas = {
  hover: z.object({
    rect: selectModeHoverRectSchema.nullable(),
  }),
  areaSelection: z.object({
    rect: selectModeBoundingRectSchema,
    items: z.array(selectableDataSchema),
  }),
  dragRect: z.object({
    dragRect: selectModeBoundingRectSchema.nullable(),
    selectionRects: z.array(selectModeSelectionRectSchema),
  }),
  inlineEditState: z.object({
    activeMarks: selectModeTiptapActiveMarksSchema,
    // Whether an inline text edit is currently open. Lets the parent show the
    // inline formatting controls (and live mark state) only while editing.
    isInlineEditing: z.boolean(),
    isInlineEditPending: z.boolean(),
  }),
};

export const websiteBuilderToPreviewIframeMethodConfig = {
  // Sent only to runtimes that list SECTION_CANVAS_CAPABILITY; null turns the canvas controls off.
  setSectionCanvas: {
    inputSchema: z.object({ canvas: sectionCanvasStateSchema.nullable() }),
    outputSchema: z.void(),
  },
  // Keeps a dropped section where it landed until the preview repaints, or puts it back.
  settleSectionMove: {
    inputSchema: z.object({
      moveId: z.string(),
      outcome: z.enum(["persisted", "failed"]),
    }),
    outputSchema: z.void(),
  },
  expandToSection: {
    inputSchema: z.object({ targetSelector: z.string() }),
    outputSchema: z.void(),
  },
  setSectionPreview: {
    inputSchema: z.object({ targetSelector: z.string().nullable() }),
    outputSchema: z.void(),
  },
  symbolicateSelectedElement: {
    inputSchema: selectedElementSchema,
    outputSchema: selectedElementSchema,
    timeoutMs: 1_500,
  },
  // Client-side transition command; fire-and-forget because success is observed as the `replo:navigated` echo.
  navigateToRoute: {
    inputSchema: z.object({ pathname: z.string() }),
    outputSchema: z.void(),
  },
  setDisplayMode: {
    inputSchema: z.object({ displayMode: displayModeSchema }),
    outputSchema: z.void(),
  },
  // Declarative push of the parent's current selection into the preview
  // iframe. Transitioning to `none` signals it to hide its
  // in-iframe selection frame and drop any in-progress drag — there's no
  // separate "clear" message because selection state always flows through
  // agentUiState. For an `area` selection the parent may also send the
  // selectors of the members it still holds, so an item removed in the
  // editor is dropped from the in-iframe selection too.
  setSelectionType: {
    inputSchema: z.object({
      selectionType: selectModeSelectionTypeSchema,
      selectors: z.array(z.string()).optional(),
    }),
    outputSchema: z.void(),
  },
  applyOptimisticUpdate: {
    inputSchema: z.object({
      editId: z.string(),
      payload: selectModeOptimisticUpdatePayloadSchema,
      targetSelector: z.string(),
      targetMode: z.enum(["primary", "group"]),
    }),
    outputSchema: z.void(),
  },
  markCommitted: {
    inputSchema: z.object({
      editId: z.string(),
      sessionId: z.string(),
    }),
    outputSchema: z.void(),
  },
  resolveSessionOverrides: {
    inputSchema: z.object({
      sessionId: z.string(),
      outcome: z.enum(["persisted", "failed"]),
    }),
    outputSchema: z.void(),
  },
  revertOverride: {
    inputSchema: z.object({
      editId: z.string(),
    }),
    outputSchema: z.void(),
  },
  enterEditMode: {
    inputSchema: z.undefined(),
    outputSchema: z.void(),
  },
  exitEditMode: {
    inputSchema: z.undefined(),
    outputSchema: z.void(),
  },
  formatCommand: {
    inputSchema: z.object({
      command: selectModeFormatCommandSchema,
    }),
    outputSchema: z.void(),
  },
};

export const previewIframeToWebsiteBuilderMethodConfig = {
  // Section canvas methods are sent only after the parent's `setSectionCanvas`, so older parents never see them.
  sectionCanvasRequest: {
    inputSchema: z.object({ request: sectionCanvasRequestSchema }),
    outputSchema: z.void(),
  },
  sectionCanvasLayoutChanged: {
    inputSchema: z.object({
      sections: z.array(sectionCanvasLayoutEntrySchema),
    }),
    outputSchema: z.void(),
  },
  requestInlineTextEdit: {
    inputSchema: z.object({
      editId: z.string(),
      element: selectedElementSchema,
    }),
    outputSchema: z.object({ allowed: z.boolean() }),
    timeoutMs: 5_000,
  },
  iframeReady: {
    inputSchema: iframeRuntimeBuildMetadataSchema,
    outputSchema: z.void(),
  },
  elementSelected: {
    inputSchema: z.object({
      data: selectableDataSchema,
    }),
    outputSchema: z.void(),
  },
  hoverElement: {
    inputSchema: selectModeEventInputSchemas.hover,
    outputSchema: z.void(),
  },
  selectedElementRectUpdate: {
    inputSchema: z.object({
      rect: selectModeBoundingRectSchema,
      clickPosition: selectModeClickPositionSchema,
    }),
    outputSchema: z.void(),
  },
  selectedAreaRectUpdate: {
    inputSchema: z.object({
      rect: selectModeBoundingRectSchema,
    }),
    outputSchema: z.void(),
  },
  dragRectUpdated: {
    inputSchema: selectModeEventInputSchemas.dragRect,
    outputSchema: z.void(),
  },
  areaSelected: {
    inputSchema: selectModeEventInputSchemas.areaSelection,
    outputSchema: z.void(),
  },
  textCommitted: {
    inputSchema: z.object({
      editId: z.string(),
      html: z.string(),
      json: z.record(z.string(), z.unknown()),
      originalInnerHTML: z.string(),
    }),
    outputSchema: z.void(),
  },
  inlineEditFeedbackChanged: {
    inputSchema: z.object({ feedback: inlineEditFeedbackSchema.nullable() }),
    outputSchema: z.void(),
  },
  editorStateUpdate: {
    inputSchema: selectModeEventInputSchemas.inlineEditState,
    outputSchema: z.void(),
  },
  fastRefreshComplete: {
    inputSchema: z.undefined(),
    outputSchema: z.void(),
  },
};

// Thin Zod unions for typegen + the editor→miniapp relay hop. Only void
// methods appear here (valued methods use channel.call directly).
export const selectModeParentMessageSchema = z
  .discriminatedUnion("method", [
    z.object({
      method: z.literal("expandToSection"),
      data: websiteBuilderToPreviewIframeMethodConfig.expandToSection
        .inputSchema,
    }),
    z.object({
      method: z.literal("setSectionPreview"),
      data: websiteBuilderToPreviewIframeMethodConfig.setSectionPreview
        .inputSchema,
    }),
    z.object({
      method: z.literal("setDisplayMode"),
      data: websiteBuilderToPreviewIframeMethodConfig.setDisplayMode
        .inputSchema,
    }),
    z.object({
      method: z.literal("setSelectionType"),
      data: websiteBuilderToPreviewIframeMethodConfig.setSelectionType
        .inputSchema,
    }),
    z.object({
      method: z.literal("applyOptimisticUpdate"),
      data: websiteBuilderToPreviewIframeMethodConfig.applyOptimisticUpdate
        .inputSchema,
    }),
    z.object({
      method: z.literal("markCommitted"),
      data: websiteBuilderToPreviewIframeMethodConfig.markCommitted.inputSchema,
    }),
    z.object({
      method: z.literal("resolveSessionOverrides"),
      data: websiteBuilderToPreviewIframeMethodConfig.resolveSessionOverrides
        .inputSchema,
    }),
    z.object({
      method: z.literal("revertOverride"),
      data: websiteBuilderToPreviewIframeMethodConfig.revertOverride
        .inputSchema,
    }),
    z.object({
      method: z.literal("enterEditMode"),
    }),
    z.object({
      method: z.literal("exitEditMode"),
    }),
    z.object({
      method: z.literal("formatCommand"),
      data: websiteBuilderToPreviewIframeMethodConfig.formatCommand.inputSchema,
    }),
  ])
  .describe("SelectModeParentMessage");

export const selectModeIframeMessageSchema = z
  .discriminatedUnion("method", [
    z.object({
      method: z.literal("iframeReady"),
      data: iframeRuntimeBuildMetadataSchema,
    }),
    z.object({
      method: z.literal("elementSelected"),
      data: previewIframeToWebsiteBuilderMethodConfig.elementSelected
        .inputSchema,
    }),
    z.object({
      method: z.literal("hoverElement"),
      data: previewIframeToWebsiteBuilderMethodConfig.hoverElement.inputSchema,
    }),
    z.object({
      method: z.literal("selectedElementRectUpdate"),
      data: previewIframeToWebsiteBuilderMethodConfig.selectedElementRectUpdate
        .inputSchema,
    }),
    z.object({
      method: z.literal("selectedAreaRectUpdate"),
      data: previewIframeToWebsiteBuilderMethodConfig.selectedAreaRectUpdate
        .inputSchema,
    }),
    z.object({
      method: z.literal("dragRectUpdated"),
      data: previewIframeToWebsiteBuilderMethodConfig.dragRectUpdated
        .inputSchema,
    }),
    z.object({
      method: z.literal("areaSelected"),
      data: previewIframeToWebsiteBuilderMethodConfig.areaSelected.inputSchema,
    }),
    z.object({
      method: z.literal("textCommitted"),
      data: previewIframeToWebsiteBuilderMethodConfig.textCommitted.inputSchema,
    }),
    z.object({
      method: z.literal("inlineEditFeedbackChanged"),
      data: previewIframeToWebsiteBuilderMethodConfig.inlineEditFeedbackChanged
        .inputSchema,
    }),
    z.object({
      method: z.literal("editorStateUpdate"),
      data: previewIframeToWebsiteBuilderMethodConfig.editorStateUpdate
        .inputSchema,
    }),
    z.object({
      method: z.literal("fastRefreshComplete"),
    }),
  ])
  .describe("SelectModeIframeMessage");

export type SelectModeParentMessageInputOf<
  MethodType extends SelectModeParentMessage["method"],
> =
  Extract<SelectModeParentMessage, { method: MethodType }> extends {
    data: infer DataType;
  }
    ? DataType
    : void;

export type SelectModeIframeMessageInputOf<
  MethodType extends SelectModeIframeMessage["method"],
> =
  Extract<SelectModeIframeMessage, { method: MethodType }> extends {
    data: infer DataType;
  }
    ? DataType
    : void;
